import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { FileText, Plus, Calendar, Tag, Sparkles, Download, CheckSquare, Square, Trash2, RotateCcw, Clock, AlertTriangle, Scale, Lock } from 'lucide-react';
import jsPDF from 'jspdf';
import { addPdfHeader, addPdfFooterToAllPages, ensureImagesLoaded } from '../utils/pdfBranding';
import { usePlan } from '../context/PlanContext';
import { useSyncRefresh, notifySyncUpdate } from '../hooks/useSyncRefresh';

import API from '../utils/api';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const CATEGORIES = ['School', 'Medical', 'Exchanges', 'Communication', 'Violations', 'Expenses', 'Behavioral Concerns', 'Safety Issues', 'Court-Related', 'Other'];

const COLOR_GRADES = {
  Red: { hex: '#EF4444', label: 'Dangerous / Abusive' },
  Orange: { hex: '#F97316', label: 'High Conflict' },
  Yellow: { hex: '#EAB308', label: 'Concerning' },
  Blue: { hex: '#3B82F6', label: 'Neutral' },
  Green: { hex: '#10B981', label: 'Positive' },
};

function gradeClasses(score) {
  var g = COLOR_GRADES[score] || COLOR_GRADES.Blue;
  var h = g.hex;
  return {
    hex: h,
    label: g.label,
    pill: 'border px-2 py-0.5 rounded-full text-xs font-medium',
    pillStyle: { borderColor: h + '66', backgroundColor: h + '22', color: h },
    barStyle: { backgroundColor: h },
  };
}

const CATEGORY_COLORS = {
  School: '#3B82F6',
  Medical: '#EF4444',
  Exchanges: '#10B981',
  Communication: '#5A4E9E',
  Violations: '#F97316',
  'Court-Related': '#2D2560',
};

function catPillStyle(cat) {
  var c = CATEGORY_COLORS[cat] || '#71717A';
  return { borderColor: c + '4D', backgroundColor: c + '1A', color: c };
}

function addEntryToPDF(pdf, entry, yStart, pw) {
  var y = yStart;
  var maxY = pdf.internal.pageSize.getHeight() - 48;
  function chk(n) { if (y + n > maxY) { pdf.addPage(); y = 20; } }
  var g = COLOR_GRADES[entry.original_score] || COLOR_GRADES.Blue;
  var gradeLabel = (entry.original_score || 'Blue') + ' - ' + g.label;

  chk(30);
  pdf.setFont('times', 'bold'); pdf.setFontSize(14); pdf.setTextColor(0, 0, 0);
  pdf.text(entry.title || 'Untitled', 15, y); y += 9;
  pdf.setFont('times', 'normal'); pdf.setFontSize(10); pdf.setTextColor(100, 100, 100);
  var meta = 'Category: ' + entry.category + '  |  Date: ' + new Date(entry.date || entry.created_at).toLocaleString() + '  |  Rating: ' + gradeLabel;
  pdf.text(meta, 15, y); y += 10;

  if (entry.rewritten_text) {
    chk(20); pdf.setFont('times', 'bold'); pdf.setFontSize(10); pdf.setTextColor(80, 80, 80);
    pdf.text('AI Rewritten (Neutral for Court):', 15, y); y += 7;
    pdf.setFont('times', 'normal'); pdf.setTextColor(0, 0, 0);
    pdf.splitTextToSize(entry.rewritten_text, pw - 30).forEach(function(ln) { chk(7); pdf.text(ln, 15, y); y += 7; });
    y += 4;
  }
  chk(20); pdf.setFont('times', 'bold'); pdf.setFontSize(10); pdf.setTextColor(80, 80, 80);
  pdf.text('Original Entry:', 15, y); y += 7;
  pdf.setFont('times', 'normal'); pdf.setTextColor(0, 0, 0);
  pdf.splitTextToSize(entry.entry_text, pw - 30).forEach(function(ln) { chk(7); pdf.text(ln, 15, y); y += 7; });
  y += 8; chk(2);
  pdf.setDrawColor(200, 200, 200); pdf.line(15, y, pw - 15, y); y += 10;
  return y;
}

function pdfHeader(pdf) {
  return addPdfHeader(pdf);
}
function pdfFooter(pdf) {
  addPdfFooterToAllPages(pdf);
}

const Journal = () => {
  const [entries, setEntries] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [batchMode, setBatchMode] = useState(false);
  const [formData, setFormData] = useState({ title: '', category: '', entry_text: '', date: new Date().toISOString().slice(0, 16) });
  // When the form was opened from a Video Call "Add transcript to journal" click,
  // we lock the date field (it must come from the actual call time, not a user
  // pick), keep a small banner, and preserve the source for backend tagging.
  const [callPrefill, setCallPrefill] = useState(null);
  const [grammarError, setGrammarError] = useState(null);
  const [limitError, setLimitError] = useState(null);
  const [viewTab, setViewTab] = useState('entries');
  const [deletedEntries, setDeletedEntries] = useState([]);
  const { getUsage, plan } = usePlan();

  useEffect(function() { fetchEntries(); fetchDeleted(); }, []);

  // Pick up a "Add transcript to journal" prefill from sessionStorage (set by
  // VideoCalls.jsx). Auto-opens the create form, locks the date to the call's
  // start time, defaults the category to "Communication", and prepends a
  // disclosure line so any reader knows the entry was derived from a call.
  useEffect(function() {
    try {
      var raw = window.sessionStorage.getItem('parentum_journal_prefill');
      if (!raw) return;
      var pf = JSON.parse(raw);
      window.sessionStorage.removeItem('parentum_journal_prefill');
      if (!pf || pf.source !== 'video_call' || !pf.transcript_text) return;

      var callDate = pf.started_at ? new Date(pf.started_at) : new Date();
      var localIso = new Date(callDate.getTime() - callDate.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
      var partner = pf.partner_name || 'co-parent';
      var reference = 'Journal entry generated from call transcript.';

      setCallPrefill(pf);
      setFormData({
        title: 'Call transcript with ' + partner + ' — ' + callDate.toLocaleDateString(),
        category: 'Communication',
        entry_text: reference,
        date: localIso,
      });
      setShowForm(true);
    } catch (e) { /* prefill ignored if parse fails */ }
  }, []);

  // Sync: refresh on tab focus, app resume, network reconnect, and cross-tab updates
  var refreshAll = useCallback(function() { fetchEntries(); fetchDeleted(); }, []);
  useSyncRefresh(refreshAll);

  var fetchEntries = async function() {
    try { var r = await axios.get(API + '/journal', { withCredentials: true }); setEntries(r.data); }
    catch (e) { console.error(e); }
  };

  var fetchDeleted = async function() {
    try { var r = await axios.get(API + '/journal/recently-deleted', { withCredentials: true }); setDeletedEntries(r.data); }
    catch (e) { console.error(e); }
  };

  var handleCreate = async function(e) {
    e.preventDefault(); setLoading(true); setGrammarError(null); setLimitError(null);
    try {
      var payload = Object.assign({}, formData);
      if (callPrefill) {
        // Tag the backend doc with provenance so admins/users can see this
        // entry came from a video call transcript.
        payload.source = 'video_call';
        payload.source_room_sid = callPrefill.room_sid;
      }
      var r = await axios.post(API + '/journal', payload, { withCredentials: true });
      if (r.data && r.data.grammar_flag && r.data.id === 'grammar-blocked') {
        setGrammarError(r.data);
        return;
      }
      setFormData({ title: '', category: '', entry_text: '', date: new Date().toISOString().slice(0, 16) });
      setCallPrefill(null);
      setShowForm(false); setGrammarError(null); fetchEntries(); notifySyncUpdate();
    } catch (err) {
      if (err.response && (err.response.status === 429 || err.response.status === 403)) {
        setLimitError(err.response.data.detail);
      } else { console.error(err); }
    }
    finally { setLoading(false); }
  };

  var handleDelete = async function(id) {
    if (!window.confirm('Move this journal entry to Recently Deleted? You can restore it within 30 days.')) return;
    try {
      await axios.delete(API + '/journal/' + id, { withCredentials: true });
      var ns = new Set(selected); ns.delete(id); setSelected(ns);
      fetchEntries(); fetchDeleted(); notifySyncUpdate();
    } catch (err) { console.error(err); }
  };

  var handleRestore = async function(id) {
    try {
      await axios.post(API + '/journal/' + id + '/restore', {}, { withCredentials: true });
      fetchEntries(); fetchDeleted(); notifySyncUpdate();
    } catch (err) { console.error(err); }
  };

  var handlePermanentDelete = async function(id) {
    if (!window.confirm('Permanently delete this entry? This action is irreversible.')) return;
    try {
      await axios.delete(API + '/journal/' + id + '/permanent', { withCredentials: true });
      fetchDeleted();
    } catch (err) { console.error(err); }
  };

  var toggle = function(id) { var ns = new Set(selected); if (ns.has(id)) ns.delete(id); else ns.add(id); setSelected(ns); };
  var selAll = function() { if (selected.size === entries.length) setSelected(new Set()); else setSelected(new Set(entries.map(function(e) { return e.id; }))); };

  var exportBatch = async function() {
    var sel = entries.filter(function(e) { return selected.has(e.id); });
    if (!sel.length) return;
    await ensureImagesLoaded();
    var pdf = new jsPDF(); var pw = pdf.internal.pageSize.getWidth();
    pdf.setFont('times', 'normal');
    var startY = pdfHeader(pdf);
    pdf.setFont('times', 'bold'); pdf.setFontSize(12); pdf.setTextColor(100, 100, 100);
    pdf.text('Journal Entries Report (' + sel.length + ' entries)', 15, startY);
    pdf.setFont('times', 'normal'); pdf.setFontSize(9); pdf.text('Generated: ' + new Date().toLocaleString(), 15, startY + 7);
    var y = startY + 16;
    sel.forEach(function(entry) { y = addEntryToPDF(pdf, entry, y, pw); });
    pdfFooter(pdf); pdf.save('parentum-journal-batch-' + Date.now() + '.pdf');
  };

  return (
    <div className="space-y-6" data-testid="journal-page">
      {/* Usage Meter for Free Users */}
      {plan === 'free' && (function() {
        var usage = getUsage('journal_entry');
        if (!usage || usage.unlimited) return null;
        var remaining = Math.max(usage.limit - usage.used, 0);
        return (
          <div className="bg-[#F4F2FB] border border-[color:var(--border-subtle)] rounded-xl p-4 flex items-center justify-between" data-testid="journal-usage-meter">
            <div className="flex items-center gap-3">
              <FileText className="w-4 h-4 text-[#5A4E9E]" />
              <div>
                <p className="text-sm font-medium">Free Plan: {remaining} journal {remaining === 1 ? 'entry' : 'entries'} remaining this month</p>
                <p className="text-xs text-[color:var(--text-soft)]">Resets on the 1st of each month. Upgrade for unlimited entries.</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-24 h-2 bg-[#F4F2FB] rounded-full overflow-hidden">
                <div className="h-full rounded-full" style={{ width: Math.round((usage.used / usage.limit) * 100) + '%', background: remaining === 0 ? '#EF4444' : 'linear-gradient(90deg, #2D2560, #5A4E9E)' }}></div>
              </div>
              <span className="text-xs text-[color:var(--text-soft)]">{usage.used}/{usage.limit}</span>
            </div>
          </div>
        );
      })()}

      {/* Header */}
      <div className="card-surface p-6">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <FileText className="w-6 h-6 text-[#5A4E9E]" />
              <h2 className="text-2xl font-bold">Journal</h2>
            </div>
            <p className="text-[color:var(--text-muted)]">Document events with AI-assisted neutral language and court-ready timestamps</p>
          </div>
          <div className="flex items-center gap-3">
            {entries.length > 0 && (
              <button
                onClick={function() { setBatchMode(!batchMode); setSelected(new Set()); }}
                data-testid="batch-mode-toggle"
                className={batchMode
                  ? 'px-4 py-2 rounded-lg font-medium transition-colors flex items-center gap-2 text-sm bg-[#5A4E9E]/20 text-[#5A4E9E] border border-[#5A4E9E]/30'
                  : 'px-4 py-2 rounded-lg font-medium transition-colors flex items-center gap-2 text-sm bg-[#F4F2FB] border border-[color:var(--border-subtle)] text-[color:var(--text-muted)] hover:bg-[#F8F7FC]'}
              >
                <CheckSquare className="w-4 h-4" />
                {batchMode ? 'Cancel Selection' : 'Select Entries'}
              </button>
            )}
            <button onClick={function() { setShowForm(!showForm); }} data-testid="new-entry-btn"
              className="px-4 py-2 bg-gradient-to-r from-[#2D2560] to-[#5A4E9E] text-white hover:opacity-90 font-semibold rounded-lg transition-opacity flex items-center gap-2">
              <Plus className="w-5 h-5" /> New Entry
            </button>
          </div>
        </div>
      </div>

      {/* Batch actions bar */}
      {batchMode && selected.size > 0 && (
        <div className="card-surface p-4 flex items-center justify-between flex-wrap gap-3" data-testid="batch-actions-bar">
          <span className="text-sm text-[color:var(--text-muted)]">{selected.size} entry(ies) selected</span>
          <div className="flex gap-3">
            <button onClick={selAll} className="px-3 py-1.5 text-sm bg-[#F4F2FB] border border-[color:var(--border-subtle)] rounded-lg hover:bg-[#F8F7FC] transition-colors" data-testid="select-all-btn">
              {selected.size === entries.length ? 'Deselect All' : 'Select All'}
            </button>
            <button onClick={exportBatch} className="px-4 py-1.5 text-sm bg-gradient-to-r from-[#2D2560] to-[#5A4E9E] text-white rounded-lg hover:opacity-90 flex items-center gap-2" data-testid="batch-pdf-btn">
              <Download className="w-4 h-4" /> Export {selected.size} to PDF
            </button>
          </div>
        </div>
      )}

      {/* Create form */}
      {showForm && (
        <div className="card-surface p-6">
          <h3 className="text-lg font-bold mb-4 flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-[#2D2560]" /> Create Journal Entry
          </h3>
          {callPrefill && (
            <div className="mb-4 rounded-xl bg-[#5A4E9E]/10 border border-[#5A4E9E]/30 p-3 flex items-start gap-2" data-testid="journal-call-prefill-banner">
              <Sparkles className="w-4 h-4 text-[#5A4E9E] mt-0.5 flex-shrink-0" />
              <div className="text-xs text-[color:var(--text-secondary)]">
                <p className="font-semibold text-[color:var(--text-primary)]">Verbatim video call transcript — locked record</p>
                <p className="mt-0.5">The transcript body and date are locked exactly as captured. No AI rewrite, no grammar check, no scoring. You can only edit the <span className="font-semibold text-[color:var(--text-primary)]">title</span> and <span className="font-semibold text-[color:var(--text-primary)]">category</span> to file this entry.</p>
              </div>
            </div>
          )}
          <form onSubmit={handleCreate} className="space-y-4" data-testid="journal-create-form">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-2">Title</label>
                <input type="text" value={formData.title}
                  onChange={function(e) { setFormData(Object.assign({}, formData, { title: e.target.value })); }}
                  className="w-full bg-[#F4F2FB] border border-[color:var(--border-subtle)] text-[color:var(--text-primary)] placeholder:text-[color:var(--text-soft)] focus:ring-2 focus:ring-[#5A4E9E] focus:border-transparent rounded-lg px-4 py-2"
                  placeholder="Brief description" required data-testid="journal-title-input" />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">
                  Date & Time
                  {callPrefill && <span className="ml-2 text-[10px] uppercase tracking-wider text-[#5A4E9E] font-semibold">Locked · from call</span>}
                </label>
                <input type="datetime-local" value={formData.date}
                  onChange={function(e) { if (!callPrefill) setFormData(Object.assign({}, formData, { date: e.target.value })); }}
                  readOnly={!!callPrefill}
                  disabled={!!callPrefill}
                  className={'w-full bg-[#F4F2FB] border border-[color:var(--border-subtle)] text-[color:var(--text-primary)] focus:ring-2 focus:ring-[#5A4E9E] focus:border-transparent rounded-lg px-4 py-2 ' + (callPrefill ? 'opacity-60 cursor-not-allowed' : '')}
                  required data-testid="journal-date-input" />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Category</label>
              <select value={formData.category}
                onChange={function(e) { setFormData(Object.assign({}, formData, { category: e.target.value })); }}
                className="w-full bg-[#F4F2FB] border border-[color:var(--border-subtle)] text-[color:var(--text-primary)] focus:ring-2 focus:ring-[#5A4E9E] focus:border-transparent rounded-lg px-4 py-2"
                required
                data-testid="journal-category-select">
                <option value="" disabled hidden className="bg-white text-[#1E1A3E]">Pick a child-welfare category</option>
                {CATEGORIES.map(function(cat) { return <option key={cat} value={cat} className="bg-white text-[#1E1A3E]">{cat}</option>; })}
              </select>
            </div>
            {callPrefill ? (
              <div>
                <label className="block text-sm font-medium mb-2">
                  Attached Transcript
                  <span className="ml-2 text-[10px] uppercase tracking-wider text-[#5A4E9E] font-semibold">PDF · locked</span>
                </label>
                <div className="rounded-lg bg-[#F4F2FB] border border-[color:var(--border-subtle)] p-4 flex items-start gap-3" data-testid="journal-attached-transcript-card">
                  <div className="w-10 h-10 rounded-lg bg-[#5A4E9E]/20 border border-[#5A4E9E]/40 flex items-center justify-center flex-shrink-0">
                    <FileText className="w-5 h-5 text-[#5A4E9E]" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-[color:var(--text-primary)] truncate">Verbatim video call transcript</p>
                    <p className="text-xs text-[color:var(--text-muted)] mt-0.5">
                      {callPrefill.partner_name ? 'with ' + callPrefill.partner_name + ' · ' : ''}
                      {callPrefill.started_at ? new Date(callPrefill.started_at).toLocaleString() : ''}
                    </p>
                    <p className="text-xs text-[color:var(--text-soft)] mt-1">The PDF is attached as-is. No AI rewrite, no grammar check, no scoring — only the title and category are editable.</p>
                  </div>
                </div>
              </div>
            ) : (
              <div>
                <label className="block text-sm font-medium mb-2">Entry</label>
                <textarea value={formData.entry_text}
                  onChange={function(e) { setFormData(Object.assign({}, formData, { entry_text: e.target.value })); }}
                  className="w-full h-48 bg-[#F4F2FB] border border-[color:var(--border-subtle)] text-[color:var(--text-primary)] placeholder:text-[color:var(--text-soft)] focus:ring-2 focus:ring-[#5A4E9E] focus:border-transparent rounded-lg p-4 resize-none"
                  placeholder="Write what happened. AI will help rewrite it objectively." required data-testid="journal-entry-textarea" />
              </div>
            )}
            {grammarError && (
              <div className="bg-[#F97316]/5 border border-[#F97316]/30 rounded-xl p-4 space-y-3" data-testid="grammar-block-error">
                <div className="flex items-start gap-2">
                  <AlertTriangle className="w-5 h-5 text-[#F97316] flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-semibold text-[#F97316]">Grammar Issues Detected — Entry Not Saved</p>
                    <p className="text-xs text-[color:var(--text-muted)] mt-1">Please correct the grammar issues below before submitting. Entries with significant grammar problems cannot be recorded to ensure accurate court documentation.</p>
                  </div>
                </div>
                {grammarError.grammar_note && (
                  <div className="bg-[#F4F2FB] rounded-lg p-3">
                    <p className="text-xs text-[color:var(--text-secondary)]">{grammarError.grammar_note}</p>
                  </div>
                )}
                {grammarError.rewritten_text && (
                  <div className="bg-[#F4F2FB] rounded-lg p-3">
                    <p className="text-xs text-[color:var(--text-soft)] uppercase tracking-wider mb-1">AI's Best Interpretation</p>
                    <p className="text-xs text-[color:var(--text-secondary)]">{grammarError.rewritten_text}</p>
                  </div>
                )}
              </div>
            )}
            {limitError && (
              <div className="bg-[#EF4444]/5 border border-[#EF4444]/30 rounded-xl p-4" data-testid="limit-error">
                <div className="flex items-start gap-2">
                  <Lock className="w-5 h-5 text-[#EF4444] flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-semibold text-[#EF4444]">Limit Reached</p>
                    <p className="text-xs text-[color:var(--text-muted)] mt-1">{limitError}</p>
                  </div>
                </div>
              </div>
            )}
            <div className="flex gap-3 pb-16 sm:pb-0 relative z-20">
              <button type="submit" disabled={loading}
                className="flex-1 bg-gradient-to-r from-[#2D2560] to-[#5A4E9E] text-white hover:opacity-90 font-semibold rounded-lg py-3 transition-opacity disabled:opacity-50"
                data-testid="journal-submit-btn">
                {loading ? 'Creating...' : 'Create Entry with AI Rewrite'}
              </button>
              <button type="button" onClick={function() { setShowForm(false); setCallPrefill(null); }}
                className="px-6 bg-[#F4F2FB] border border-[color:var(--border-subtle)] text-[color:var(--text-primary)] hover:bg-[#F8F7FC] rounded-lg transition-colors">Cancel</button>
            </div>
          </form>
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-2" data-testid="journal-tabs">
        <button onClick={function() { setViewTab('entries'); }}
          className={viewTab === 'entries' ? 'px-4 py-2 rounded-lg text-sm font-medium bg-[#5A4E9E]/20 text-[#5A4E9E] border border-[#5A4E9E]/30' : 'px-4 py-2 rounded-lg text-sm font-medium bg-[#F4F2FB] border border-[color:var(--border-subtle)] text-[color:var(--text-muted)] hover:bg-[#F8F7FC]'}
          data-testid="journal-tab-entries">
          Entries {entries.length > 0 && <span className="ml-1 text-xs">({entries.length})</span>}
        </button>
        <button onClick={function() { setViewTab('deleted'); }}
          className={viewTab === 'deleted' ? 'px-4 py-2 rounded-lg text-sm font-medium bg-red-500/20 text-red-400 border border-red-500/30' : 'px-4 py-2 rounded-lg text-sm font-medium bg-[#F4F2FB] border border-[color:var(--border-subtle)] text-[color:var(--text-muted)] hover:bg-[#F8F7FC]'}
          data-testid="journal-tab-deleted">
          Recently Deleted {deletedEntries.length > 0 && <span className="ml-1 text-xs">({deletedEntries.length})</span>}
        </button>
      </div>

      {/* Color grade legend */}
      {viewTab === 'entries' && entries.length > 0 && (
        <div className="card-surface p-4" data-testid="color-grade-legend">
          <p className="text-xs font-medium text-[color:var(--text-soft)] mb-2 uppercase tracking-wider">AI Co-Parenting Rating</p>
          <div className="flex flex-wrap gap-3">
            {Object.entries(COLOR_GRADES).map(function([key, val]) {
              return (
                <div key={key} className="flex items-center gap-2 px-3 py-1 rounded-full text-xs border"
                  style={{ borderColor: val.hex + '66', backgroundColor: val.hex + '22', color: val.hex }}>
                  <span className="w-2 h-2 rounded-full" style={{ backgroundColor: val.hex }}></span>
                  {key}: {val.label}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Entries list */}
      {viewTab === 'entries' && (
      <div className="space-y-4">
        {entries.length === 0 ? (
          <div className="card-surface p-12 text-center" data-testid="journal-empty">
            <FileText className="w-12 h-12 text-[color:var(--text-soft)] mx-auto mb-3" />
            <p className="text-[color:var(--text-muted)]">No journal entries yet</p>
          </div>
        ) : entries.map(function(entry) {
          var gc = gradeClasses(entry.original_score);
          var isSel = selected.has(entry.id);
          return (
            <div key={entry.id}
              className={isSel ? 'card-surface p-6 transition-all border-[#5A4E9E]/40 ring-1 ring-[#5A4E9E]/20' : 'card-surface p-6 transition-all hover:border-[#A8AAD4]'}
              data-testid={'journal-entry-' + entry.id}>
              <div className="flex items-start gap-3">
                {batchMode && (
                  <button onClick={function() { toggle(entry.id); }} className="mt-1 flex-shrink-0" data-testid={'select-entry-' + entry.id}>
                    {isSel ? <CheckSquare className="w-5 h-5 text-[#5A4E9E]" /> : <Square className="w-5 h-5 text-[color:var(--text-soft)] hover:text-[color:var(--text-muted)]" />}
                  </button>
                )}
                <div className="w-1 self-stretch rounded-full flex-shrink-0" style={gc.barStyle}></div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex-1 min-w-0">
                      <h3 className="text-lg font-semibold mb-1">{entry.title}</h3>
                      <div className="flex items-center gap-3 text-sm text-[color:var(--text-muted)] flex-wrap">
                        <span className="flex items-center gap-1">
                          <Calendar className="w-4 h-4" />
                          {new Date(entry.date || entry.created_at).toLocaleString()}
                        </span>
                        <span className="px-2 py-0.5 rounded-full text-xs border" style={catPillStyle(entry.category)}>{entry.category}</span>
                        {entry.source === 'video_call' ? (
                          <span className="px-2 py-0.5 rounded-full text-xs border border-[#5A4E9E]/40 bg-[#5A4E9E]/10 text-[#5A4E9E] font-semibold uppercase tracking-wider" data-testid={'journal-locked-pill-' + entry.id}>
                            Locked transcript
                          </span>
                        ) : (
                          <span className={gc.pill} style={gc.pillStyle}>{entry.original_score || 'Blue'}</span>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0 ml-2 flex-wrap">
                      <button onClick={function() { handleDelete(entry.id); }}
                        className="px-3 py-1 bg-red-500/10 border border-red-500/30 text-red-400 hover:bg-red-500/20 rounded-lg transition-colors flex items-center gap-2 text-sm"
                        data-testid={'delete-btn-' + entry.id}>
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                  {entry.rewritten_text && (
                    <div className="bg-[#F4F2FB] border border-[color:var(--border-subtle)] rounded-lg p-4 mb-3">
                      <p className="text-sm text-[color:var(--text-soft)] mb-2 flex items-center gap-1"><Tag className="w-3 h-3" /> AI Rewritten (Neutral for Court)</p>
                      <p className="text-[color:var(--text-primary)]">{entry.rewritten_text}</p>
                    </div>
                  )}
                  {entry.grammar_flag && (
                    <div className="bg-[#F97316]/5 border border-[#F97316]/20 rounded-lg p-3 mb-3 flex items-start gap-2" data-testid="grammar-flag">
                      <AlertTriangle className="w-4 h-4 text-[#F97316] flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="text-xs font-medium text-[#F97316]">Grammar Issue Detected</p>
                        {entry.grammar_note && <p className="text-xs text-[color:var(--text-muted)] mt-0.5">{entry.grammar_note}</p>}
                      </div>
                    </div>
                  )}
                  {entry.plan_note && (
                    <div className="bg-[#5A4E9E]/5 border border-[#5A4E9E]/20 rounded-lg p-3 mb-3 flex items-start gap-2" data-testid="plan-note">
                      <Scale className="w-4 h-4 text-[#5A4E9E] flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="text-xs font-medium text-[#5A4E9E]">Master Plan Reference</p>
                        <p className="text-xs text-[color:var(--text-muted)] mt-0.5">{entry.plan_note}</p>
                      </div>
                    </div>
                  )}
                  {entry.source === 'video_call' && entry.source_room_sid ? (
                    <div className="mt-2 rounded-lg bg-[#F4F2FB] border border-[color:var(--border-subtle)] p-4 flex items-center gap-3" data-testid={'journal-pdf-attachment-' + entry.id}>
                      <div className="w-10 h-10 rounded-lg bg-[#5A4E9E]/20 border border-[#5A4E9E]/40 flex items-center justify-center flex-shrink-0">
                        <FileText className="w-5 h-5 text-[#5A4E9E]" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold text-[color:var(--text-primary)] truncate">Video call transcript</p>
                        <p className="text-xs text-[color:var(--text-muted)]">{entry.entry_text}</p>
                      </div>
                      <a
                        href={BACKEND_URL + '/api/video/rooms/' + entry.source_room_sid + '/download/transcript'}
                        target="_blank"
                        rel="noreferrer"
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[#5A4E9E] text-white text-xs font-semibold hover:bg-[#2D2560] transition-colors flex-shrink-0"
                        data-testid={'journal-download-transcript-' + entry.id}
                      >
                        <Download className="w-3.5 h-3.5" /> Download PDF
                      </a>
                    </div>
                  ) : (
                    <details className="group">
                      <summary className="cursor-pointer text-sm text-[color:var(--text-muted)] hover:text-[color:var(--text-secondary)] transition-colors list-none flex items-center gap-2">
                        <span className="group-open:rotate-90 transition-transform inline-block">&#9654;</span>
                        View original entry
                      </summary>
                      <div className="mt-2 bg-[#F4F2FB] border border-[color:var(--border-subtle)] rounded-lg p-4">
                        <p className="text-[color:var(--text-secondary)]">{entry.entry_text}</p>
                      </div>
                    </details>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
      )}

      {/* Recently Deleted */}
      {viewTab === 'deleted' && (
        <div className="space-y-4" data-testid="journal-recently-deleted">
          {deletedEntries.length === 0 ? (
            <div className="card-surface p-12 text-center">
              <Trash2 className="w-12 h-12 text-[color:var(--text-soft)] mx-auto mb-3" />
              <p className="text-[color:var(--text-muted)]">No recently deleted entries</p>
              <p className="text-sm text-[color:var(--text-soft)] mt-1">Deleted entries appear here for 30 days before permanent removal</p>
            </div>
          ) : deletedEntries.map(function(entry) {
            return (
              <div key={entry.id} className="card-surface p-4 border-red-500/10" data-testid={'deleted-entry-' + entry.id}>
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <h4 className="font-medium text-[color:var(--text-secondary)]">{entry.title}</h4>
                    <div className="flex items-center gap-3 text-xs text-[color:var(--text-soft)] mt-1 flex-wrap">
                      <span>{entry.category}</span>
                      <span className="flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        Deleted {entry.deleted_at ? new Date(entry.deleted_at).toLocaleDateString() : '—'}
                      </span>
                      <span className="text-red-400">
                        {entry.days_remaining > 0 ? entry.days_remaining + ' days until permanent deletion' : 'Purging soon'}
                      </span>
                    </div>
                    {entry.entry_text && (
                      <p className="text-sm text-[color:var(--text-soft)] mt-2 line-clamp-2">{entry.entry_text.slice(0, 150)}{entry.entry_text.length > 150 ? '...' : ''}</p>
                    )}
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <button onClick={function() { handleRestore(entry.id); }}
                      className="px-3 py-1.5 bg-cyan-500/10 border border-cyan-500/30 text-cyan-400 hover:bg-cyan-500/20 rounded-lg transition-colors text-xs flex items-center gap-1"
                      data-testid={'restore-btn-' + entry.id}>
                      <RotateCcw className="w-3.5 h-3.5" /> Restore
                    </button>
                    <button onClick={function() { handlePermanentDelete(entry.id); }}
                      className="px-3 py-1.5 bg-red-500/10 border border-red-500/30 text-red-400 hover:bg-red-500/20 rounded-lg transition-colors text-xs flex items-center gap-1"
                      data-testid={'perm-delete-btn-' + entry.id}>
                      <AlertTriangle className="w-3.5 h-3.5" /> Delete Forever
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default Journal;
